<template>
	<div id="item-plan">
		<!-- Filter Card -->
		<v-card flat color="lightgray">
			<!-- Filter inputs -->
			<v-row dense class="ma-0 px-4 pt-2">
				<!-- From date -->
				<v-col cols="6">
					<v-dialog v-model="fromDialog" max-width="290px">
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								v-bind="attrs"
								v-on="on"
								:value="fromDate"
								:label="$t('ItemPlan.from-date')"
								readonly
								dense
								hide-details
								outlined
								prepend-inner-icon="mdi-calendar-start"
							/>
						</template>
						<v-date-picker
							v-model="fromDate"
							:max="toDate || undefined"
							@input="fromDialog = false"
						/>
					</v-dialog>
				</v-col>
				<!-- To date -->
				<v-col cols="6">
					<v-dialog v-model="toDialog" max-width="290px">
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								v-bind="attrs"
								v-on="on"
								:value="toDate"
								:label="$t('ItemPlan.to-date')"
								readonly
								dense
								hide-details
								outlined
								prepend-inner-icon="mdi-calendar-end"
							/>
						</template>
						<v-date-picker
							v-model="toDate"
							:min="fromDate || undefined"
							@input="toDialog = false"
						/>
					</v-dialog>
				</v-col>
				<!-- Plan name filter -->
				<v-col cols="12">
					<v-autocomplete
						v-model="planName"
						:items="itemPlanMaterials"
						item-text="planName"
						item-value="planName"
						:label="$T('inputs.name')"
						dense
						hide-details
						outlined
						clearable
						prepend-inner-icon="mdi-magnify"
					/>
				</v-col>
				<!-- Specialty -->
				<v-col v-if="0" cols="6">
					<v-autocomplete
						v-model="filterSpecialtyId"
						:items="specialties"
						item-text="name"
						item-value="id"
						:label="$T('inputs.specialty')"
						dense
						hide-details
						outlined
						clearable
					/>
				</v-col>
				<!-- Workload -->
				<v-col v-if="0" cols="6">
					<v-autocomplete
						v-model="filterWorkloadId"
						:items="workloads"
						item-text="name"
						item-value="id"
						:label="$T('inputs.workload')"
						dense
						hide-details
						outlined
						clearable
					/>
				</v-col>
				<!-- Entity / Customer -->
				<v-col v-if="0" cols="6">
					<v-autocomplete
						v-model="filterEntityId"
						:items="entities"
						item-text="name"
						item-value="id"
						:label="$T('inputs.customer-name')"
						dense
						hide-details
						outlined
						clearable
					/>
				</v-col>
			</v-row>

			<!-- Search & Reset buttons -->
			<v-card-actions class="px-4 pb-3">
				<v-btn
					color="primary"
					small
					@click="search"
				>
					<v-icon small class="me-1">mdi-magnify</v-icon>
					{{ $t('ItemPlan.search') }}
				</v-btn>
				<v-btn
					outlined
					color="grey darken-1"
					small
					@click="reset"
				>
					<v-icon small class="me-1">mdi-refresh</v-icon>
					{{ $t('ItemPlan.reset') }}
				</v-btn>
				<!-- <v-spacer /> -->
			</v-card-actions>
		</v-card>

		<loader v-if="loading" />
		<v-container fluid v-else>
			<v-expansion-panels v-if="itemPlanMaterials.length">
				<!-- Plan level -->
				<v-expansion-panel
					v-for="planItem in itemPlanMaterials"
					:key="planItem.id"
				>
					<v-expansion-panel-header class="px-3">
						<div class="me-2 flex-grow-0">
							<v-icon>mdi-clipboard-text-outline</v-icon>
						</div>
						<div class="font-weight-bold">{{ planItem.planName }}</div>
					</v-expansion-panel-header>
					<v-expansion-panel-content class="px-0">
						<!-- Specialty level -->
						<v-expansion-panels v-if="planItem.specialties.length" accordion flat>
							<v-expansion-panel
								v-for="specialtyItem in planItem.specialties"
								:key="specialtyItem.id"
							>
								<v-expansion-panel-header class="px-3">
									<div class="me-2 flex-grow-0">
										<v-icon>mdi-pill</v-icon>
									</div>
									<div>{{ specialtyItem.specialtyName }}</div>
								</v-expansion-panel-header>
								<v-expansion-panel-content class="px-0">
									<!-- Materials Table -->
									<v-simple-table v-if="specialtyItem.materials.length" dense>
										<template v-slot:default>
											<thead>
												<tr class="primary">
													<th class="white--text col-material">{{ $t('ItemPlan.material-name') }}</th>
													<th class="white--text text-end col-quantity">{{ $t('ItemPlan.quantity') }}</th>
													<th class="white--text col-notes">{{ $T('inputs.notes') }}</th>
												</tr>
											</thead>
											<tbody>
												<tr
													v-for="material in specialtyItem.materials"
													:key="material.id"
												>
													<td class="col-material">
														<span class="font-weight-bold text-body-2">
															{{ material.materialName || getMaterialById(material.materialId)?.name }}
														</span>
														<span class="ms-1 text-caption grey--text">
															({{ getMaterialFormById(getMaterialById(material.materialId)?.materialFormId)?.name || '' }})
														</span>
													</td>
													<td class="text-end col-quantity">{{ $options.filters.formatNumber(material.amount, 3) }}</td>
													<td class="col-notes">{{ material.notes || '-' }}</td>
												</tr>
											</tbody>
										</template>
									</v-simple-table>

									<!-- No Materials Message -->
									<div v-else class="text-center pa-4 grey--text text-body-2">
										{{ $t('ItemPlan.no-items-to-show') }}
									</div>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>

						<!-- No Specialties Message -->
						<div v-else class="text-center pa-4 grey--text text-body-2">
							{{ $t('ItemPlan.no-items-to-show') }}
						</div>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>

			<!-- Empty State -->
			<div
				v-else
				class="d-flex flex-column justify-center align-center"
				style="height: 45vh"
			>
				<v-icon x-large class="mb-3">mdi-pill</v-icon>
				<span class="d-flex flex-column justify-center align-center grey--text text-body-1">
					{{ $t('ItemPlan.no-items-to-show') }}
				</span>
			</div>
		</v-container>
	</div>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapState } from 'vuex'
import Loader from '@/components/Loader.vue'

export default {
	name: 'ItemPlan',

	components: { Loader },

	data() {
		return {
			moment,
			loading: false,
			fromDialog: false,
			toDialog: false,
			fromDate: moment().startOf('month').format('YYYY-MM-DD'),
			toDate: moment().endOf('month').format('YYYY-MM-DD'),

			filterSpecialtyId: null,
			filterWorkloadId: null,
			filterEntityId: null,
			planName: null,
		}
	},

	computed: {
		...mapState({
			specialties: state => state.specialties.specialties,
			plans: state => state.plans.plans,
			rawItemPlanMaterials: state => state.materials.specialtyMaterials,
			workloads: state => state.workloads.workloads,
			materials: state => state.materials.materials,
			materialForms: state => state.materialForms.materialForms,
			entities: state => state.entities.entities,
		}),

		...mapGetters({
			getSpecialtyById: 'specialties/getSpecialtyById',
			getWorkloadById: 'workloads/getWorkloadById',
			getMaterialById: 'materials/getMaterialById',
			getMaterialFormById: 'materialForms/getMaterialFormById'
		}),

		itemPlanMaterials() {
			if (!this.rawItemPlanMaterials || !this.rawItemPlanMaterials.length) {
				return []
			}

			// Group hierarchy: plan → specialty → materials
			const plansMap = {}

			this.rawItemPlanMaterials.forEach(specialtyGroup => {
				const specialtyId = specialtyGroup.specialtyId
				const specialtyName = specialtyGroup.specialtyName || this.getSpecialtyById(specialtyId)?.name || 'Unknown'

				// Flatten all materials (workload + direct) for this specialty group
				const materials = []
				if (specialtyGroup.workloads && specialtyGroup.workloads.length) {
					specialtyGroup.workloads.forEach(workload => {
						workload.materials.forEach(material => {
							materials.push({
								...material,
								workloadId: workload.workloadId,
								workloadName: workload.workloadName
							})
						})
					})
				}
				if (specialtyGroup.materials && specialtyGroup.materials.length) {
					materials.push(...specialtyGroup.materials)
				}

				// Distribute each material into its plan → specialty bucket
				materials.forEach(material => {
					const planKey = material.planId != null ? material.planId : (material.planName || 'no-plan')
					const planName = material.planName || this.$t('ItemPlan.unnamed-plan')

					if (!plansMap[planKey]) {
						plansMap[planKey] = {
							planId: material.planId ?? null,
							id: `plan-${planKey}`,
							planName,
							specialties: {}
						}
					}

					const plan = plansMap[planKey]
					if (!plan.specialties[specialtyId]) {
						plan.specialties[specialtyId] = {
							specialtyId,
							id: `plan-${planKey}-specialty-${specialtyId}`,
							specialtyName,
							materials: []
						}
					}

					plan.specialties[specialtyId].materials.push(material)
				})
			})

			return Object.values(plansMap).map(plan => ({
				...plan,
				specialties: Object.values(plan.specialties)
			}))
		}
	},

	methods: {
		search() {
			this.loading = true
			this.$store.dispatch('materials/fetchAllForSpecialtyVisits', {
				fromDate: this.fromDate,
				toDate: this.toDate,
				specialtyId: this.filterSpecialtyId || undefined,
				workloadId: this.filterWorkloadId || undefined,
				entityId: this.filterEntityId || undefined,
				planName: this.planName || undefined,
			}).finally(() => {
				this.loading = false
			})
		},

		reset() {
			this.filterSpecialtyId = null
			this.filterWorkloadId = null
			this.filterEntityId = null
			this.planName = null
			this.search()
		},
	},

	created() {
		this.loading = true
		Promise.all([
			!this.specialties.length ? this.$store.dispatch('specialties/fetchAll') : null,
			!this.workloads.length ? this.$store.dispatch('workloads/fetchAll') : null,
			!this.materialForms.length ? this.$store.dispatch('materialForms/fetchAll') : null,
			!this.plans.length ? this.$store.dispatch('plans/fetchAll') : null,
			this.$store.dispatch('materials/fetchAll', {}),
			!this.entities.length ? this.$store.dispatch('entities/fetchAll', {}) : null,
			!this.itemPlanMaterials.length
				? this.$store.dispatch('materials/fetchAllForSpecialtyVisits', {
					fromDate: moment().startOf('month').format('YYYY-MM-DD'),
					toDate: moment().endOf('month').format('YYYY-MM-DD'),
				})
				: null,
		]).finally(() => {
			this.loading = false
		})
	}
}
</script>

<style lang="scss" scoped>
#item-plan {
	.v-expansion-panel-content__wrap {
		padding: 0
	}

	.v-card {
		border: 1px solid #e0e0e0
	}

	// Nested specialty panels inside a plan panel
	.v-expansion-panel-content .v-expansion-panels {
		.v-expansion-panel-header {
			background-color: #fafafa
		}
	}

	.col-material {
		width: 25%
	}

	.col-quantity {
		width: 15%
	}

	.col-notes {
		width: 60%
	}
}
</style>
